import uuid from 'uuid';

import JsonApi, {getBaseDomainUrl} from "../../api/jsonApi";
import {getCommonVisualizerType} from "../../config";
import {setMultiLoader} from "../../actions/actionFunctions";
import {hideGroupObject, toBase64} from "./aiFunctions";
import {
  setAILoading,
  setAIResults,
  setLookAiImageOpened,
  setLookAiRenderFinished,
  setLookAiWaitingBoxClicked
} from "./aiActions";
import {LOOK_OVERLAYS} from "../look/core/constants";
import {getImagePromiseFromLayout} from "../../misc/getCanvasImageFromLayout";
import {getCurrentLayout} from "../appLayout";
import {convertImgToBase64URLNoCors} from "../../misc/utils";
import {playAudio} from "../../misc/audioCache";

const newSwaggerHeaders = {
  'Accept': '*',
  'Content-Type': 'application/json'
}

const successSoundUrl = "https://qc-cdn.s3.dualstack.us-east-1.amazonaws.com/sounds/finish_tiered_chime.wav";

export const getCurrentPrompts = async () => {
  const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/Prompt`);
  const convertResponse = await rawResponse.json();
  console.log("convertResponse", convertResponse);
  if (!Array.isArray(convertResponse)) {
    return []
  }
  const visualizerType = getCommonVisualizerType();
  const filteredResponse = convertResponse.filter(prObject => prObject.tool === visualizerType);
  let firstActiveIndex = filteredResponse.findIndex(prObject => prObject.isDefault && (prObject.type === "First"));
  let secondActiveIndex = filteredResponse.findIndex(prObject => prObject.isDefault && (prObject.type === "Second"));
  let thirdActiveIndex = filteredResponse.findIndex(prObject => prObject.isDefault && (prObject.type == "Third"));
  if (firstActiveIndex === -1) {
    firstActiveIndex = filteredResponse.findIndex(prObject => prObject.type === "First"); // set at least something active
  }
  if (secondActiveIndex === -1) {
    secondActiveIndex = filteredResponse.findIndex(prObject => prObject.type === "Second"); // set at least something active
  }
  if (thirdActiveIndex === -1) {
    thirdActiveIndex = filteredResponse.findIndex(prObject => prObject.type === "Third"); // set at least something active
  }
  if (firstActiveIndex > -1) {
    filteredResponse[firstActiveIndex]["isActiveFirst"] = true;
  }
  if (secondActiveIndex > -1) {
    filteredResponse[secondActiveIndex]["isActiveSecond"] = true;
  }
  if (thirdActiveIndex > -1) {
    filteredResponse[thirdActiveIndex]["isActiveThird"] = true;
  }
  return filteredResponse
}

export const removePrompt = (pr) => {
  return fetch(`${getBaseDomainUrl()}:7122/api/Prompt/${pr.id}`, {
    method: 'DELETE',
  });
}

export const editPrompt = (pr) => {
  return fetch(`${getBaseDomainUrl()}:7122/api/Prompt`, {
    method: 'PATCH',
    headers: newSwaggerHeaders,
    body: JSON.stringify(pr)
  });
}

export const createPrompt = (postData) => {
  return fetch(`${getBaseDomainUrl()}:7122/api/Prompt`, {
    method: 'POST',
    headers: newSwaggerHeaders,
    body: JSON.stringify(postData)
  });
}

export const setDefaultPrompts = async (activePromptFirst, activePromptSecond, activePromptThird) => {
  if (activePromptFirst) {
    await fetch(`${getBaseDomainUrl()}:7122/api/Prompt/default/${activePromptFirst.id}`, {
      method: 'PATCH',
      headers: newSwaggerHeaders,
    })
  }
  if (activePromptSecond) {
    await fetch(`${getBaseDomainUrl()}:7122/api/Prompt/default/${activePromptSecond.id}`, {
      method: 'PATCH',
      headers: newSwaggerHeaders,
    })
  }
  if (activePromptThird) {
    await fetch(`${getBaseDomainUrl()}:7122/api/Prompt/default/${activePromptThird.id}`, {
      method: 'PATCH',
      headers: newSwaggerHeaders,
    })
  }
}

export const postGenerateAiImage = (popupContext, signal) => {
  return async (dispatch, getState) => {
    const {groupObjects, userFile, promptsList} = popupContext;
    const storeState = getState();
    const {canvasId} = storeState.bathroomVisualizerState;
    const {aiResults} = storeState.aiState;

    dispatch(setAILoading(true));
    dispatch(setMultiLoader({ activeStep: 0, visible: true, finished: false }));

    const featureDefinitionIDs = groupObjects
      .filter(groupObject => groupObject.enabled && !hideGroupObject(groupObject))
      .map(groupObject => {
        return groupObject.featureGroup.activeFeature.dbFeature.featureDefinitionID
      });

    const base64Code = await toBase64(userFile);

    const activePromptFirst = promptsList.find(promptObject => promptObject.isActiveFirst);
    const activePromptSecond = promptsList.find(promptObject => promptObject.isActiveSecond);
    const activePromptThird = promptsList.find(promptObject => promptObject.isActiveThird);
    if (!activePromptFirst) {
      return
    }

    await setDefaultPrompts(activePromptFirst, activePromptSecond, activePromptThird);
    const jobId = document.kvConfig.jobId || storeState.serverState.jobId;

    const postData = {
      "fabricatorId": document.kvConfig.userId,
      "featureDefinitionIds": featureDefinitionIDs,
      "jobId": jobId,
      "userPhoto": {
        "b64File": base64Code,
        "fileName": userFile.name,
      },
      "examplePhoto": {
        "b64File": document.getElementById(canvasId).toDataURL('image/jpeg').split(',')[1],
        "fileName": "reference.jpg"
      },
      "tool": getCommonVisualizerType(),
      "firstPromptVersion": activePromptFirst.version,
      "secondPromptVersion": activePromptSecond ? activePromptSecond.version : 0,
      "thirdPromptVersion": activePromptThird ? activePromptThird.version : 0
    }

    console.log("postData", postData);
    try {
      const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/ImageProcessing/convert`, {
        method: 'POST',
        headers: newSwaggerHeaders,
        body: JSON.stringify(postData),
        signal,
      });
      const convertResponse = await rawResponse.json();
      await playAudio(successSoundUrl);
      dispatch(setAILoading(false));
      dispatch(setAIResults([...aiResults, convertResponse]));
      console.log("convertResponse", convertResponse);
      return convertResponse ? convertResponse.resultImageUrl : "";
    } catch (e) {
      dispatch(setAILoading(false));
      return "";
    }
  }
}

export const postGenerateLookAiImage = (lookAiPopupContext, signal) => {
  return async (dispatch, getState) => {

    const {userFile, groupObjects} = lookAiPopupContext;
    const {serverState, bathroomVisualizerState} = getState();

    dispatch(setAILoading(true));

    if (serverState.isMobile) {
      const scrollDiv = document.querySelector(".look-ai__content");
      const loadingDiv = document.querySelector(".look-ai__sidebar-content--dropzone");
      if (loadingDiv && scrollDiv?.scrollTop > 120) {
        loadingDiv.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    const featureDefinitionIDs = groupObjects
      .filter(groupObject => groupObject.enabled && !hideGroupObject(groupObject))
      .map(groupObject => {
        return groupObject.featureGroup.activeFeature.dbFeature.featureDefinitionID
      });

    const base64Code = await toBase64(userFile);
    const canvasElement = document.getElementById(bathroomVisualizerState.canvasId);
    const imageData = canvasElement
      ? canvasElement.toDataURL('image/jpeg')
      : await getImagePromiseFromLayout(getCurrentLayout(), null, 1)

    const postData = {
      "fabricatorId": document.kvConfig.userId,
      "featureDefinitionIds": featureDefinitionIDs,
      "jobId": document.kvConfig.jobId || serverState.jobId,
      "userPhoto": {
        "b64File": base64Code,
        "fileName": userFile.name,
      },
      "examplePhoto": {
        "b64File": (imageData || "").split(',')[1],
        "fileName": "reference.jpg"
      },
      "tool": getCommonVisualizerType(),
      // "firstPromptVersion": 0, // do not pass - we should use default ones
      // "secondPromptVersion": 0 // do not pass - we should use default ones
    }

    console.log("postData", postData);
    try {
      const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/ImageProcessing/convert`, {
        method: 'POST',
        headers: newSwaggerHeaders,
        body: JSON.stringify(postData),
        signal,
      });
      if (!rawResponse.ok) {
        const errorText = await rawResponse.text(); // fallback to raw text
        throw new Error(errorText || `Failed to generate image. Please try again later`);
      }
      const convertResponse = await rawResponse.json();

      const storeState = getState();
      const {aiResults, aiUserFileBlobUrl} = storeState.aiState;
      const {lookOverlayOpened} = storeState.lookState;

      const nextAiResults = aiResults.map(aiResult => {
        return Object.assign({}, aiResult, {isActive: false});
      })

      convertResponse.id = uuid.v4();
      convertResponse.isActive = true;
      convertResponse.sourceImageUrl = aiUserFileBlobUrl;

      await playAudio(successSoundUrl);

      dispatch(setAILoading(false));
      dispatch(setLookAiWaitingBoxClicked(true));
      if (nextAiResults.every(res => res.resultImageUrl)) {
        dispatch(setAIResults([...nextAiResults, convertResponse]));
      } else {
        const nextUnrenderedIndex = nextAiResults.findIndex(res => !res.resultImageUrl);
        if (nextUnrenderedIndex > -1) {
          nextAiResults[nextUnrenderedIndex] = convertResponse;
          dispatch(setAIResults([...nextAiResults]));
        }
      }

      if (lookOverlayOpened === LOOK_OVERLAYS.aiPanel) {
        dispatch(setLookAiImageOpened(true))
      } else {
        dispatch(setLookAiRenderFinished(true))
      }

      console.log("convertResponse", convertResponse);
      return convertResponse ? convertResponse.resultImageUrl : "";
    } catch (e) {
      // await new Promise(resolve => setTimeout(resolve, 5000)); // devtest // need to change api url to get here
      const storeState = getState();
      const {aiResults} = storeState.aiState;
      const nextAiResults = aiResults.map(aiResult => {
        return Object.assign({}, aiResult, {isActive: false});
      })
      const convertResponse = {
        id: uuid.v4(),
        isActive: true,
        // sourceImageUrl: storeState.aiState.aiUserFileBlobUrl, // devtest
        // resultImageUrl: "https://ai-visualizing.s3.amazonaws.com/image-246030-c7a7fd2b-cca3-4e4a-b13c-26db4e2038dc.jpg", // devtest
        conversionError: e.message === "signal is aborted without reason" ? "Aborted manually" : e.message,
      }
      if (nextAiResults.every(res => res.resultImageUrl)) {
        dispatch(setAIResults([...nextAiResults, convertResponse]));
      } else {
        const nextUnrenderedIndex = nextAiResults.findIndex(res => !res.resultImageUrl);
        if (nextUnrenderedIndex > -1) {
          nextAiResults[nextUnrenderedIndex] = convertResponse;
          dispatch(setAIResults([...nextAiResults]));
        }
      }
      // //devtest
      // const {lookOverlayOpened} = storeState.lookState;
      // if (lookOverlayOpened === LOOK_OVERLAYS.aiPanel) {
      //   dispatch(setLookAiImageOpened(true))
      // } else {
      //   dispatch(setLookAiRenderFinished(true))
      // } // devtest
      dispatch(setAILoading(false));
      dispatch(setLookAiImageOpened(false)); // do not open image if there is an error
      dispatch(setLookAiWaitingBoxClicked(true));
      // return "https://ai-visualizing.s3.amazonaws.com/image-246030-c7a7fd2b-cca3-4e4a-b13c-26db4e2038dc.jpg"; // devtest
    }
  }
}

// const sendImageToOpenAI = async (base64Image) => {
//   try {
//     // Convert base64 to blob
//     const byteString = atob(base64Image.split(',')[1]);
//     const mimeString = base64Image.split(',')[0].split(':')[1].split(';')[0];
//     const ab = new ArrayBuffer(byteString.length);
//     const ia = new Uint8Array(ab);
//     for (let i = 0; i < byteString.length; i++) {
//       ia[i] = byteString.charCodeAt(i);
//     }
//     const blob = new Blob([ab], { type: mimeString });
//
//     const file = new File([blob], 'image.png', { type: mimeString });
//
//     // Create form data
//     const formData = new FormData();
//     formData.append('image', file);
//     formData.append('n', '1'); // number of images to return
//     formData.append('size', '512x512'); // size can be 256x256, 512x512, 1024x1024
//
//     // Send to OpenAI API (proxy this through your backend in production!)
//     const response = await axios.post(
//       'https://api.openai.com/v1/images/variations',
//       formData,
//       {
//         headers: {
//           // 'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
//           'Content-Type': 'multipart/form-data',
//         },
//       }
//     );
//
//     return response.data.data[0].url; // Returns a URL to the new image
//   } catch (error) {
//     console.error('Error sending image to OpenAI:', error);
//     throw error;
//   }
// };

export const getCurrentPromptParts = async (promptObject) => {
  const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/prompt/${promptObject.id}`);
  const convertResponse = await rawResponse.json();
  console.log("promptPartsResponse", convertResponse);
  if (!Array.isArray(convertResponse)) {
    return []
  }
  return convertResponse
}

export const updatePromptParts = (groupObject, promptObject, type) => {
  const isActiveBody = "partBody" + type;
  const savedPromptPartObject = "savedPromptPartObject" + type;

  if (groupObject[savedPromptPartObject]) { // update
    return fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/${groupObject[savedPromptPartObject].id}`, {
      method: 'PUT',
      headers: newSwaggerHeaders,
      body: JSON.stringify(Object.assign({}, groupObject[savedPromptPartObject], {
        partBody: groupObject[isActiveBody],
      }))
    });
  } else { // create
    return fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/list`, {
      method: 'POST',
      headers: newSwaggerHeaders,
      body: JSON.stringify([{
        "id": 0,
        "promptId": promptObject.id,
        "featureGroup": groupObject.featureGroup.dbName || groupObject.featureGroup.partName,
        "partBody": groupObject[isActiveBody] || ""
      }])
    });
  }
}

export const saveCreatedAIImage = async (jobId, aiState) => {
  try {
    if (jobId && aiState.aiResults[0] && !aiState.aiResults[0].conversionError && aiState.aiResults[0].resultImageUrl) {
      const sourceImageUrl = await convertImgToBase64URLNoCors(aiState.aiResults[0].sourceImageUrl);
      const resultImageUrl = await convertImgToBase64URLNoCors(aiState.aiResults[0].resultImageUrl);
      // no await here, to open faster the ai image result
      await JsonApi.postSaveJobDocument([
        {
          "fileName": "user_photo.png",
          "base64Content": (sourceImageUrl || "").replace(/^data:image\/(png|jpg);base64,/, ""),
          "documentType": "user_room_photo",
          "jobId": jobId
        },
        {
          "fileName": "generated_photo.png",
          "base64Content": (resultImageUrl || aiState.aiResults[0].resultImageUrl || "").replace(/^data:image\/(png|jpg);base64,/, ""),
          "documentType": "ai_room_photo",
          "jobId": jobId
        }
      ]);
      // dispatch(setAIResults(aiState.aiResults.map((aiRes, aiResIndex) => {
      //   if (aiResIndex === 0) {
      //     return Object.assign({}, aiRes, {isSavedOnBackend: true});
      //   }
      //   return aiRes
      // })))
    }
  } catch (error) {
    console.log("saveCreatedAIImage error", error);
  }
  return Promise.resolve();
}
